import { ref, computed, watch } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { useAuthStore } from './auth';
import { loadAllPermissions, loadRolePermissions } from '@/services/permissions-service';
import { ORGANIZATION_ROLES, type PermissionRow, type RolePermissionRow } from '@/types/organizations';

// Cache for all permissions (never changes, so load once)
let cachedAllPermissions: PermissionRow[] | null = null;
let allPermissionsPromise: Promise<PermissionRow[]> | null = null;

async function getAllPermissions(): Promise<PermissionRow[]> {
    if (cachedAllPermissions) {
        return cachedAllPermissions;
    }
    if (allPermissionsPromise) {
        return allPermissionsPromise;
    }
    allPermissionsPromise = loadAllPermissions();
    cachedAllPermissions = await allPermissionsPromise;
    return cachedAllPermissions;
}

// Helper to get allowed permission names for a given org and role
// Uses already-loaded role permissions to avoid duplicate API calls
function computeUserPermissions(
    rolePerms: RolePermissionRow[],
    allPerms: PermissionRow[],
    userRole: string,
    isSuperAdmin: boolean,
): string[] {
    // If user is superadmin, grant all permissions
    if (isSuperAdmin) {
        return allPerms.map((p) => p.name);
    }

    // Otherwise, use normal role-based permissions
    const allowedPermIds = rolePerms.filter((rp) => rp.role === userRole && rp.allowed).map((rp) => rp.permissionId);
    return allPerms.filter((p) => allowedPermIds.includes(p.id)).map((p) => p.name);
}

export const usePermissionsStore = createGlobalState(() => {
    const permissions = ref<string[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);

    // Separate loading state for role permissions
    const rolePermissionsLoading = ref(false);
    const userRolePermissions = ref<RolePermissionRow[]>([]);

    // Track what we've loaded to avoid duplicate calls
    const lastLoadedOrgId = ref<string | null>(null);
    const lastLoadedRole = ref<string | null>(null);
    const lastLoadedUserId = ref<string | null>(null);

    const { organizationId, role, user } = useAuthStore();

    async function loadPermissionsForOrgAndRole(orgId: string, userRole: string) {
        if (!orgId || !userRole || !user.value) {
            if (!orgId || !userRole) {
                console.warn(
                    '[permissions.store] loadPermissionsForOrgAndRole called with undefined orgId or userRole',
                );
            }
            return;
        }

        // Check if we already have the data loaded
        const userId = user.value.id;
        if (
            lastLoadedOrgId.value === orgId &&
            lastLoadedRole.value === userRole &&
            lastLoadedUserId.value === userId &&
            userRolePermissions.value.length > 0 &&
            permissions.value.length > 0
        ) {
            // Already loaded, skip
            return;
        }

        loading.value = true;
        rolePermissionsLoading.value = true;
        error.value = null;

        // Clear permissions immediately when switching orgs
        if (lastLoadedOrgId.value !== orgId) {
            permissions.value = [];
            userRolePermissions.value = [];
        }

        try {
            // Load only the current user's role permissions alongside all permission definitions
            const rolePermsPromise =
                lastLoadedOrgId.value === orgId &&
                lastLoadedRole.value === userRole &&
                userRolePermissions.value.length > 0
                    ? Promise.resolve(userRolePermissions.value)
                    : loadRolePermissions(orgId, userRole);

            const [rolePerms, allPerms] = await Promise.all([rolePermsPromise, getAllPermissions()]);

            userRolePermissions.value = rolePerms;

            // Check if user is a superadmin (admin role in default organization)
            const isSuperAdmin =
                user.value?.app_metadata?.organizations?.[import.meta.env.VITE_DEFAULT_ORGANIZATION_ID]?.[0] ===
                'admin';

            permissions.value = computeUserPermissions(rolePerms, allPerms, userRole, isSuperAdmin);

            lastLoadedOrgId.value = orgId;
            lastLoadedRole.value = userRole;
            lastLoadedUserId.value = userId;
        } catch (e: any) {
            error.value = e.message || 'Failed to load permissions';
            permissions.value = [];
            userRolePermissions.value = [];
        } finally {
            loading.value = false;
            rolePermissionsLoading.value = false;
        }
    }

    function hasPermission(permission: string | string[], requireAll: boolean = false): boolean {
        if (Array.isArray(permission)) {
            return requireAll
                ? permission.every((p) => permissions.value.includes(p))
                : permission.some((p) => permissions.value.includes(p));
        }
        return permissions.value.includes(permission);
    }

    const allowedRoles = computed(() => ORGANIZATION_ROLES);

    const userId = computed(() => user.value?.id);

    watch(
        [organizationId, role, userId],
        async ([newOrgId, newRole, newUserId], [oldOrgId, oldRole, oldUserId]) => {
            if (!newOrgId || !newRole || !newUserId) {
                return;
            }

            if (newOrgId !== oldOrgId) {
                permissions.value = [];
                userRolePermissions.value = [];
                lastLoadedOrgId.value = null;
                lastLoadedRole.value = null;
                lastLoadedUserId.value = null;
            }

            if (newOrgId !== oldOrgId || newRole !== oldRole || newUserId !== oldUserId) {
                await loadPermissionsForOrgAndRole(newOrgId, newRole);
            }
        },
        { immediate: true },
    );

    return {
        permissions,
        loading,
        error,
        hasPermission,
        loadPermissions: loadPermissionsForOrgAndRole,
        allowedRoles,
        rolePermissionsLoading,
    };
});
